import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { formatKw } from 'app/utils';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import { Icon } from 'app/components/common/Icon';
import { LeadNowVM } from 'app/stores/ui/agt.leadnow.ui.store';
import { AgtLeadNowConsecutiveDetail } from './AgtLeadNowConsecutiveDetail';

export const LeadNowGpConsecutiveListe = observer(() => {
	const { berichteUiiStore, agtLeadNowUiStore, agenturListUiStore } = useStore();
	const location = useLocation();
	const history = useHistory();
	const [hasItems, setHasItems] = useState(false);

	const selectedAgtId = useMemo(() => {
		const params = new URLSearchParams(location.search);
		const id = params.get('detailAgtId');
		return id ? parseInt(id) : null;
	}, [location.search]);

	const tm = useMemo(() => {
		const tm = new TableModel<LeadNowVM, number>();
		const cols: ITableColumn<LeadNowVM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});
		cols.push({
			label: 'Agentur Status',
			path: 'agenturStatusLabel',
			sortBy: 'agenturStatusLabel',
		});
		cols.push({
			label: 'Teilhaber',
			path: 'agt.thCount',
		});

		cols.push({
			label: 'GP (inkl. TH)',
			path: 'leadNowMultiGpCount',
			cellModifier: 'border-left-green bg-green',
			render(row) {
				return <>{row.data.leadNowMultiGpCount > 0 ? <Icon iconClass="check-circle" faModifier="far" /> : <></>}</>;
			},
			agg: [
				{
					aggType: AggregrationType.sum,
				},
			],
		});

		cols.push({
			label: '12+ Wochen',
			path: 'isConsecutive12',
			render(row) {
				return <>{row.data.isConsecutive12 === 1 ? <Icon iconClass="check" /> : <></>}</>;
			},
			agg: [
				{
					aggType: AggregrationType.sum,
					path: 'isConsecutive12',
				},
			],
		});

		cols.push({
			label: 'Anzahl Wochen',
			path: 'consecutiveCount',
			sortBy: 'consecutiveCount',
			agg: [
				{
					aggType: AggregrationType.avg,
					path: 'consecutiveCount',
				},
			],
		});

		cols.push({
			label: 'Erste KW',
			path: 'consecutiveFirstKw',
			sortBy: 'consecutiveFirstKw',
			format: formatKw,
		});

		cols.push({
			label: 'Letzte KW',
			path: 'consecutiveLastKw',
			sortBy: 'consecutiveLastKw',
			format: formatKw,
		});

		tm.sortBy = 'consecutiveCount';
		tm.sortAsc = false;
		tm.setCols(cols);

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		tm.onRowClick = (row: TableRow<LeadNowVM>) => {
			const params = new URLSearchParams(history.location.search);
			params.set('detailAgtId', row.data.agt.agtId.toString());
			history.replace(history.location.pathname + '?' + params.toString());
		};

		return tm;
	}, []);

	useEffect(() => {
		agtLeadNowUiStore.load().then(async () => {
			const maxKw = agtLeadNowUiStore.maxKw;
			if (!maxKw) return;
			let items = await agtLeadNowUiStore.loadConsectutiveData(maxKw);
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as LeadNowVM[];
				items = agenturListUiStore.applyFilterOnGpRelevantAgt(items) as LeadNowVM[];
			}

			setHasItems(items.length > 0);
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtLeadNowUiStore]);

	const handleCloseDetail = useCallback(() => {
		const params = new URLSearchParams(history.location.search);
		params.delete('detailAgtId');
		const newSearch = params.toString();
		history.replace(history.location.pathname + (newSearch ? '?' + newSearch : ''));
	}, [history]);

	return (
		<>
			<div className="table-container">
				<div className="pad-1rem">
					<div className="note-box">
						<p>
							<Icon iconClass="info-circle" />
						</p>
						<div className="note-message">Berücksichtigt werden nur Agenturen, die mindestens einen kostenpflichtigen Lead erhalten haben – Kunden-werben-Kunden (KwK) ausgenommen.</div>
					</div>
				</div>
				{!hasItems ? <div className="pad-1rem">Keine Daten vorhanden</div> : <Table stickyHeader={true} tm={tm} stickyFooter={true}></Table>}
			</div>
			{selectedAgtId && <AgtLeadNowConsecutiveDetail agtId={selectedAgtId} onClose={handleCloseDetail} />}
		</>
	);
});
